import { initAnalytics } from "@mountayaapp/analytics";

import { env } from "../../env";
import { isSessionReplayEnabled } from "./replay";

/**
 * POSTHOG_API_HOST is the ingest origin analytics falls back to when the build
 * did not set one. It is the same reverse proxy the marketing site posts to, so
 * a visitor's anonymous id carries across the parent domain from the site into
 * the application, and an ad blocker sees a first-party host.
 */
const POSTHOG_API_HOST = "https://t.mountaya.com";

/**
 * POSTHOG_UI_HOST is the PostHog application itself. Because ingest goes through
 * the proxy above, the SDK needs this separately or the links it renders would
 * point back at the proxy rather than at PostHog.
 */
const POSTHOG_UI_HOST = "https://eu.posthog.com";

/**
 * startAnalytics loads and initializes PostHog for the application, session
 * replay included. isSessionReplayEnabled owns the enablement gate, so the embed
 * surface, the Playwright suite, a build without a project token, and a device
 * belonging to an excluded person never load the SDK at all — and the map
 * surfaces that have to know whether a recorder is watching read the same answer
 * rather than a second copy of the rule.
 *
 * It runs at bootstrap rather than behind a deferral. Nothing here blocks render:
 * the SDK is a dynamic import, so it never enters the entry bundle and its fetch
 * is off the critical path already. Running first also seeds the gate's cached
 * answer from the bootstrap pathname, so the map surfaces that read it later
 * agree with the decision the SDK was actually started on.
 */
export const startAnalytics = (): void => {
  const token = env.posthogProjectToken;

  // The gate already rejects a missing token; the explicit token check is what
  // narrows it from `string | undefined` for the init call below.
  if (!isSessionReplayEnabled() || token === undefined) {
    return;
  }

  void initAnalytics({
    apiHost: env.posthogHost ?? POSTHOG_API_HOST,
    replay: true,
    token,
    uiHost: POSTHOG_UI_HOST,
  });
};
